import type {
  AiCoach_Action as AiCoachAction,
  AiCoach_CourseraContextInput as AiCoachCourseraContextInput,
} from '__generated__/graphql-types';
import { AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

import type { CourseItem } from '@coursera/event-pulse-types';

import type Item from 'bundles/author-course/models/Item';
import {
  CURRENT_ROLE_PLAY_PROMPT_VERSION,
  CURRENT_SOCRATIC_DIALOGUE_PROMPT_VERSION,
  ROLE_PLAY_PLUGIN_NAME,
  SOCRATIC_DIALOGUE_PLUGIN_NAME,
} from 'bundles/coach-item/common/constants';
import type { ProviderProps } from 'bundles/coach-item/common/contexts/useCoachItemChatContext';
import type { CoachItemPluginName } from 'bundles/coach-item/common/types';
import {
  AiEmbeddingsIndexDocumentErrorCode,
  AiPersonaBehavior,
  AuthoringMode,
  AuthoringPreviewUiMode,
  CoachItemType,
} from 'bundles/coach-item/common/types';

import _t from 'i18n!nls/coach-item';

/** Checks for plugin name match, ignoring version number */
export const pluginIsACoachItem = (pluginName: string | undefined) => {
  return pluginName === SOCRATIC_DIALOGUE_PLUGIN_NAME || pluginName === ROLE_PLAY_PLUGIN_NAME;
};

/**
 * Checks if a widget item in authoring is a dialogue
 * */
export const isWidgetItemADialogue = (item: Item) => {
  return (
    item.content.typeName === 'ungradedWidget' && item.content.definition?.iframeTitle === SOCRATIC_DIALOGUE_PLUGIN_NAME
  );
};

export const getCoachItemType = (pluginName: CoachItemPluginName | string): CoachItemType => {
  if (pluginName === SOCRATIC_DIALOGUE_PLUGIN_NAME) {
    return CoachItemType.SocraticDialogue;
  }

  if (pluginName === ROLE_PLAY_PLUGIN_NAME) {
    return CoachItemType.RolePlay;
  }

  throw new Error(`Unknown plugin name: ${pluginName}`);
};

export const versionedCoachItemType = (coachItemType: CoachItemType, promptVersion: number) => {
  return `${coachItemType}@${promptVersion}`;
};

export const unversionedCoachItemType = (versionedType: string) => {
  return versionedType.split('@')[0] as CoachItemType;
};

/** Create a random string of unreliable length */
export const randomString = () => Math.random().toString(36).slice(2);

export const getCoachItemSessionTitle = (courseId: string, itemId: string, inAuthoringMode: boolean) => {
  return `${inAuthoringMode ? 'authoring-' : ''}coachItem-${courseId}-${itemId}`;
};

export const getRolePlayIntroSessionTitle = (courseId: string, itemId: string, inAuthoringMode: boolean) => {
  return tupleToStringKey([inAuthoringMode ? 'authoring-coachItem' : 'coachItem', courseId, itemId, 'intro']);
};

export const getRolePlaySessionTitle = (
  courseId: string,
  itemId: string,
  introSessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'rolePlay',
    introSessionId,
  ]);
};

export const getRolePlayDebriefSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'debrief',
    rolePlaySessionId,
  ]);
};

export const getRolePlayHintsSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'hints',
    rolePlaySessionId,
  ]);
};

export const getRolePlayTasksSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'tasks',
    rolePlaySessionId,
  ]);
};

interface DetermineAuthoringPreviewUiModeArgs {
  uploaderHasError: boolean;
  inputErrors?: Record<string, boolean>;
}
export const determineAuthoringPreviewUiMode = ({
  uploaderHasError,
  inputErrors,
}: DetermineAuthoringPreviewUiModeArgs): AuthoringPreviewUiMode => {
  if (Object.values(inputErrors || {}).filter((hasError) => hasError).length > 0) {
    return AuthoringPreviewUiMode.MISSING_INFO;
  }

  if (uploaderHasError) {
    return AuthoringPreviewUiMode.ERROR;
  }

  return AuthoringPreviewUiMode.ACTIVE;
};

export const authoringModeToAiCoachMode = (authoringMode: AuthoringMode | undefined): AiCoachMode => {
  switch (authoringMode) {
    case AuthoringMode.DRAFT:
      return AiCoachMode.CoachItemAuthoringDraft;
    case AuthoringMode.PUBLISHED:
      return AiCoachMode.CoachItemAuthoringPublished;
    default:
      return AiCoachMode.CoachItem;
  }
};

export const getDefaultDialogueAuthoringItemTitle = () => _t('Untitled');

export const stringIsAiPersonaBehavior = (str: string | undefined): str is AiPersonaBehavior => {
  return !!str && Object.values(AiPersonaBehavior).includes(str);
};

export const getAiPersonaBehaviorDescription = (aiPersonaBehavior: AiPersonaBehavior): string => {
  switch (aiPersonaBehavior) {
    case AiPersonaBehavior.Cooperative:
      return _t('Cooperative - Acts warm, reassuring, and optimistic.');
    case AiPersonaBehavior.Skeptical:
      return _t('Skeptical - Acts friendly, assertive, and slightly pessimistic.');
    case AiPersonaBehavior.Demanding:
      return _t(
        'Demanding (Use with caution) - Acts disagreeable, hard to persuade, and is less likely to help the learner.'
      );
    default:
      return '';
  }
};

export const getCoachItemPromptVersion = (itemType: CoachItemType): number => {
  switch (itemType) {
    case CoachItemType.SocraticDialogue:
      return CURRENT_SOCRATIC_DIALOGUE_PROMPT_VERSION;
    case CoachItemType.RolePlay:
      return CURRENT_ROLE_PLAY_PROMPT_VERSION;
    default:
      throw new Error(`Unknown coach item type: ${itemType}`);
  }
};

export const getUploadErrorString = (errorCodes: AiEmbeddingsIndexDocumentErrorCode) => {
  switch (errorCodes) {
    case AiEmbeddingsIndexDocumentErrorCode.NotFoundError:
      return _t('The specified file could not be found. Please try again.');
    case AiEmbeddingsIndexDocumentErrorCode.SizeLimitExceededError:
      return _t('The file exceeds the maximum allowed size. Please reduce the file size and try again.');
    case AiEmbeddingsIndexDocumentErrorCode.UnsupportedFileTypeError:
      return _t('The file format is not supported. Please upload a file in one of the accepted formats: .txt');
    case AiEmbeddingsIndexDocumentErrorCode.ProcessingError:
    default:
      return _t('Something went wrong. Please try again or contact Coursera support if the issue persists.');
  }
};

type CourseraContextInput = Pick<
  ProviderProps,
  'authoringCourseBranchId' | 'courseBranchId' | 'courseId' | 'itemId' | 'authoringMode'
>;

export const getCourseraContext = (input: CourseraContextInput) => {
  const { authoringCourseBranchId, authoringMode, courseBranchId, courseId, itemId } = input;

  const courseraContext: AiCoachCourseraContextInput = {
    courseId,
    itemId,
  };

  if (authoringMode !== undefined) {
    courseraContext.authoringCourseBranchId = authoringCourseBranchId;
  }

  if (courseBranchId !== undefined) {
    courseraContext.courseBranchId = courseBranchId;
  }

  return courseraContext;
};

type CreateNewMessageInputType = {
  message: string;
  action: AiCoachAction;
  chatSessionId: string;
  authoringMode: AuthoringMode | undefined;
  courseraContext: AiCoachCourseraContextInput;
};

export const createNewMessageInput = (input: CreateNewMessageInputType) => {
  const { authoringMode, message, action, chatSessionId, courseraContext } = input;

  return {
    input: {
      userId: String(getUser()?.id),
      message,
      courseraContext,
      mode: authoringModeToAiCoachMode(authoringMode),
      action,
      chatSessionId,
    },
    recommendedActionsEnabled: true,
    clipRecommendationsEnabled: false,
  };
};

export const getCourseItemTypeFromCoachItemType = (coachItemType: CoachItemType): CourseItem['type'] => {
  switch (coachItemType) {
    case CoachItemType.SocraticDialogue:
      return 'dialogue';
    case CoachItemType.RolePlay:
      return 'role_play';
    default:
      throw new Error(`Unknown coach item type: ${coachItemType}`);
  }
};
